// src/pages/pedidos/PesqPedidoItemPage.js
import React, { useCallback, useEffect, useMemo, useState } from 'react';
import { FaSortDown, FaSortUp } from 'react-icons/fa';
import { fetchData } from '../../services/Api';
import { formatCurrency } from '../../utils/numberUtils';
import { formatDate, formatDateInput, formatTime } from '../../utils/dateTimeUtils';
import { useMessage } from '../../contexts/geral/MessageContext';
import PageContainer from '../../components/geral/PageContainer';
import PageHeader from '../../components/geral/PageHeader';
import PageContent from '../../components/geral/PageContent';
import PageFooter from '../../components/geral/PageFooter';
import Input from '../../components/input/Input';
import InputCheck from '../../components/input/InputCheck';
import Select from '../../components/input/Select';
import { useConfigModulo } from '../../contexts/administrativo/ConfigModuloContext';
import { getNumeroPedido } from '../../utils/pedidoUtils';
import { statusPedidoOptions } from '../../models/pedidos/StatusPedido';
import { getResumoItensPedidosPDFService } from '../../services/pedidos/PedidoService';
import { usePrinter } from '../../contexts/geral/PrinterContext';

const PesqPedidoItemPage = () => {
  const { showMessage } = useMessage();
  const { configModuloSel } = useConfigModulo();
  const { imprimirPdfBlob } = usePrinter();
  const [periodoInicial, setPeriodoInicial] = useState('');
  const [horaInicial, setHoraInicial] = useState('00:00');
  const [periodoFinal, setPeriodoFinal] = useState('');
  const [horaFinal, setHoraFinal] = useState('23:50');
  const [statusPedido, setStatusPedido] = useState(-1);
  const [produtoDescricao, setProdutoDescricao] = useState('');
  const [agruparPorProduto, setAgruparPorProduto] = useState(false);
  const [itens, setItens] = useState([]);
  const [ordenacao, setOrdenacao] = useState({ campo: null, direcao: 'asc' });
  const [gerandoResumo, setGerandoResumo] = useState(false);

  const statusCombo = [{ value: -1, label: "Todos" }, ...statusPedidoOptions];

  useEffect(() => {
    const dataAtual = new Date();
    const dataAtualFormatada = formatDateInput(dataAtual);
    setPeriodoInicial(dataAtualFormatada);
    setPeriodoFinal(dataAtualFormatada);
  }, []);

  const buscarItens = useCallback(async () => {
    try {
      const data = await fetchData('/PedidoItem/GetItensByFilter', {
        DataIni: periodoInicial ? `${periodoInicial}T${horaInicial || '00:00'}:00` : null,
        DataFim: periodoFinal ? `${periodoFinal}T${horaFinal || '23:50'}:59` : null,
        StatusPedido: statusPedido === -1 ? null : statusPedido,
        DescricaoProduto: produtoDescricao || '',
      });

      const itensMapeados = (data || [])
        .map((item) => {
          const valorUnitario = Number(item?.valUn ?? item?.valor ?? item?.valorUnitario ?? 0);
          const quantidade = Number(item?.qtde ?? item?.quantidade ?? 0);
          const valorTotal = Number(item?.valTotal ?? item?.valorTotal ?? valorUnitario * quantidade);

          return {
            pedidoId: item?.pedidoId ?? item?.PedidoId ?? item?.idPedido ?? '-',
            pedidoIdDia: item?.pedidoIdDia ?? '-',
            numeroPedido: getNumeroPedido({
              pedidoId: item?.pedidoId ?? item?.PedidoId ?? item?.idPedido ?? null,
              pedidoIdDia: item?.pedidoIdDia ?? null,
            }, configModuloSel) || '-',
            produtoId: item?.produtoId ?? item?.ProdutoId ?? item?.idProduto ?? '-',
            status: item?.status ?? item?.statusPedido ?? item?.statusPedidoDescricao ?? '-',
            dataPedido: item?.dataPedido ?? item?.dataHoraPedido ?? item?.DataPedido ?? null,
            produto: item?.produto ?? item?.nomeProduto ?? item?.produtoDescricao ?? '-',
            valUn: valorUnitario,
            qtde: quantidade,
            valTotal: valorTotal,
          };
        })
        .sort((a, b) => new Date(b.dataPedido) - new Date(a.dataPedido));

      setItens(itensMapeados);
    } catch (err) {
      showMessage(`Erro ao buscar itens dos pedidos: ${err}`, 'danger');
    }
  }, [periodoInicial, horaInicial, periodoFinal, horaFinal, statusPedido, produtoDescricao, showMessage, configModuloSel]);

  useEffect(() => {
    if (periodoInicial && horaInicial && periodoFinal && horaFinal) {
      buscarItens();
    }
  }, [periodoInicial, horaInicial, periodoFinal, horaFinal, buscarItens]);

  const itensExibidos = useMemo(() => {
    const itensBase = !agruparPorProduto
      ? itens
      : Object.values(
        itens.reduce((acc, item) => {
          const chave = String(item.produtoId ?? '-');

          if (!acc[chave]) {
            acc[chave] = {
              ...item,
              qtde: Number(item.qtde || 0),
              valTotal: Number(item.valTotal || 0),
            };
            return acc;
          }

          acc[chave].qtde += Number(item.qtde || 0);
          acc[chave].valTotal += Number(item.valTotal || 0);
          return acc;
        }, {}),
      ).map((item) => ({
        ...item,
        valUn: item.qtde > 0 ? item.valTotal / item.qtde : 0,
      }));

    const { campo, direcao } = ordenacao;

    if (!campo) {
      return itensBase;
    }

    const multiplicador = direcao === 'asc' ? 1 : -1;

    return [...itensBase].sort((a, b) => {
      const valorA = a[campo];
      const valorB = b[campo];

      if (campo === 'dataPedido') {
        return (new Date(valorA || 0) - new Date(valorB || 0)) * multiplicador;
      }

      if (['pedidoId', 'produtoId', 'valUn', 'qtde', 'valTotal'].includes(campo)) {
        return (Number(valorA || 0) - Number(valorB || 0)) * multiplicador;
      }

      return String(valorA || '').localeCompare(String(valorB || ''), 'pt-BR', {
        sensitivity: 'base',
      }) * multiplicador;
    });
  }, [agruparPorProduto, itens, ordenacao]);

  const alterarOrdenacao = (campo) => {
    setOrdenacao((atual) => ({
      campo,
      direcao: atual.campo === campo
        ? (atual.direcao === 'asc' ? 'desc' : 'asc')
        : 'asc',
    }));
  };

  const renderOrdenadorColuna = (campo, titulo, style = {}) => {
    const ativa = ordenacao.campo === campo;

    return (
      <button
        type="button"
        onClick={() => alterarOrdenacao(campo)}
        style={{
          background: 'none',
          border: 0,
          padding: 0,
          font: 'inherit',
          fontWeight: 'bold',
          color: 'inherit',
          cursor: 'pointer',
          width: '100%',
          textAlign: style.textAlign || 'left',
        }}
      >
        {titulo}
        {ativa && (ordenacao.direcao === 'asc' ? <FaSortUp /> : <FaSortDown />)}
      </button>
    );
  };

  const totalItens = useMemo(
    () => itensExibidos.reduce((total, item) => total + Number(item.valTotal || 0), 0),
    [itensExibidos]
  );

  const handleImprimir = async () => {
    if (!periodoInicial || !horaInicial || !periodoFinal || !horaFinal) {
      showMessage('Informe as datas e horas inicial e final para gerar o resumo.', 'warning');
      return;
    }

    const dataHoraInicial = `${periodoInicial}T${horaInicial}:00`;
    const dataHoraFinal = `${periodoFinal}T${horaFinal}:59`;

    if (dataHoraInicial > dataHoraFinal) {
      showMessage('A data e hora inicial não podem ser posteriores à data e hora final.', 'warning');
      return;
    }

    setGerandoResumo(true);
    try {
      const pdf = await getResumoItensPedidosPDFService({
        dataIni: dataHoraInicial,
        dataFim: dataHoraFinal,
        statusPedido: statusPedido === -1 ? null : statusPedido,
        descricaoProduto: produtoDescricao || '',
        paperWidthMm: 77,
      });
      await imprimirPdfBlob(pdf, {
        frameId: 'resumo-produtos-pedidos-pdf-print-frame',
        titulo: 'Resumo de produtos dos pedidos',
        mensagemErro: 'Não foi possível abrir a impressão do resumo de produtos dos pedidos.',
      });
    } catch (error) {
      showMessage(`Erro ao gerar resumo dos produtos: ${error?.message || error}`, 'danger');
    } finally {
      setGerandoResumo(false);
    }
  };

  return (
    <PageContainer>
      <h2 className="page-title">Pesquisa de Itens de Pedidos</h2>
      <PageHeader>
        <div className="group-div">
          <Input
            label="Periodo Inicial"
            type="date"
            value={periodoInicial}
            onChange={(e) => setPeriodoInicial(e.target.value)}
            size={150}
          />
          <Input
            label="Hora Inicial"
            type="time"
            value={horaInicial}
            onChange={(e) => setHoraInicial(e.target.value)}
            size={90}
          />
          <Input
            label="Periodo Final"
            type="date"
            value={periodoFinal}
            onChange={(e) => setPeriodoFinal(e.target.value)}
            size={150}
          />
          <Input
            label="Hora Final"
            type="time"
            value={horaFinal}
            onChange={(e) => setHoraFinal(e.target.value)}
            size={90}
          />
          <Select
            label="Status"
            value={statusPedido}
            onChange={(e) => setStatusPedido(parseInt(e.target.value, 10))}
            options={statusCombo}
            size={250}
          />
          <Input
            label="Produto"
            value={produtoDescricao}
            onChange={(e) => setProdutoDescricao(e.target.value)}
            placeholder="Descricao do produto"
            size={150}
          />
          <InputCheck
            id="agruparPorProduto"
            label="Agrupar por produto"
            checked={agruparPorProduto}
            onChange={(e) => setAgruparPorProduto(e.target.checked)}
          />
        </div>
        <button className="btn btn-search" onClick={buscarItens}>Buscar</button>
      </PageHeader>

      <PageContent>
        <table className="page-content-table">
          <thead>
            <tr>
              <th>
                {renderOrdenadorColuna(
                  agruparPorProduto ? 'produtoId' : 'pedidoId',
                  agruparPorProduto ? 'ProdutoId' : 'PedidoId',
                )}
              </th>
              <th>{renderOrdenadorColuna('status', 'Status')}</th>
              <th>{renderOrdenadorColuna('dataPedido', 'DataPedido')}</th>
              <th>{renderOrdenadorColuna('produto', 'Produto')}</th>
              <th style={{ textAlign: 'right' }}>
                {renderOrdenadorColuna('valUn', 'ValUn', { textAlign: 'right' })}
              </th>
              <th style={{ textAlign: 'right' }}>
                {renderOrdenadorColuna('qtde', 'Qtde', { textAlign: 'right' })}
              </th>
              <th style={{ textAlign: 'right' }}>
                {renderOrdenadorColuna('valTotal', 'ValTotal', { textAlign: 'right' })}
              </th>
            </tr>
          </thead>
          <tbody>
            {itensExibidos.map((item, index) => (
              <tr key={`${agruparPorProduto ? item.produtoId : item.pedidoId}-${index}`}>
                <td>{agruparPorProduto ? item.produtoId : item.numeroPedido}</td>
                <td>{item.status}</td>
                <td>
                  {item.dataPedido
                    ? `${formatDate(item.dataPedido)} ${formatTime(item.dataPedido)}`
                    : '-'}
                </td>
                <td>{item.produto}</td>
                <td style={{ textAlign: 'right' }}>{formatCurrency(item.valUn)}</td>
                <td style={{ textAlign: 'right' }}>{item.qtde}</td>
                <td style={{ textAlign: 'right' }}>{formatCurrency(item.valTotal)}</td>
              </tr>
            ))}
          </tbody>
        </table>
      </PageContent>

      <PageFooter>
        <div className="botoes-footer">
          <button
            type="button"
            className="btn btn-secondary btn-footer"
            onClick={handleImprimir}
            disabled={gerandoResumo}
          >
            {gerandoResumo ? 'Gerando...' : 'Imprimir resumo'}
          </button>
        </div>
        <div className="totais-footer">
          <span>Quantidade: {itensExibidos.length}</span>
          <span>Total: {formatCurrency(totalItens)}</span>
        </div>
      </PageFooter>

    </PageContainer>
  );
};

export default PesqPedidoItemPage;
